import {
  AutocompleteSelect,
  Button,
  Dialog,
  Icons,
  Label,
  Select,
} from "@win-reality/react-ui";
import { ReactNode, useEffect, useState } from "react";

import { CreateTeamRosterDialog } from "@/components/CreateTeamRosterDialog";
import { DEFAULT_TEAM_COLOR } from "@/lib/constants";

import { type TeamOption } from "../teamOption";
import { useTeams, useVisibilityGroups } from "./Overview.hooks";
import { TeamColorPickerModal } from "./TeamColorPickerModal";

export type { TeamOption };

const isOrgVisibilityGroup = (visibilityGroup?: string): boolean => {
  return visibilityGroup?.startsWith("ORG") ?? false;
};

type TeamRosterPickerProps = {
  label: string;
  required?: boolean;
  optional?: boolean;
  value: string | null;
  initialLabel?: string;
  initialParentName?: string | null;
  onConfirm: (option: TeamOption | null, visibilityGroup: string) => void;
  onClear?: () => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  messageSlot?: ReactNode;
  teamColor?: string;
  onColorChange?: (color: string) => void;
};

export function TeamRosterPicker({
  label,
  required,
  optional,
  value,
  initialLabel,
  initialParentName,
  onConfirm,
  onClear,
  isOpen: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  messageSlot,
  teamColor = DEFAULT_TEAM_COLOR,
  onColorChange,
}: TeamRosterPickerProps) {
  const isExternallyControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isExternallyControlled ? controlledOpen : internalOpen;
  const setIsOpen = (open: boolean) => {
    if (isExternallyControlled) {
      controlledOnOpenChange?.(open);
    } else {
      setInternalOpen(open);
    }
  };

  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);

  const [committedOption, setCommittedOption] = useState<TeamOption | null>(
    value && initialLabel
      ? {
          value,
          label: initialLabel,
          meta: { parentName: initialParentName ?? null },
        }
      : null,
  );
  const [committedVisibilityGroup, setCommittedVisibilityGroup] = useState<
    string | undefined
  >();

  const [stagedVisibilityGroup, setStagedVisibilityGroup] = useState<
    string | undefined
  >();
  const [stagedSearchQuery, setStagedSearchQuery] = useState("");
  const [stagedOption, setStagedOption] = useState<TeamOption | null>(null);

  const { visibilityGroupOptions } = useVisibilityGroups();
  const { teamOptions, isLoadingTeams } = useTeams({
    searchQuery: stagedSearchQuery,
    visibilityGroup: stagedVisibilityGroup,
  });

  useEffect(() => {
    if (!value) {
      setCommittedOption((prev) => (prev === null ? prev : null));
      return;
    }
    const match = teamOptions.find((o) => o.value === value);
    if (match) {
      setCommittedOption((prev) =>
        prev?.value === match.value && prev?.label === match.label
          ? prev
          : match,
      );
    } else if (initialLabel) {
      setCommittedOption((prev) =>
        prev?.value === value &&
        prev?.label === initialLabel &&
        (prev?.meta?.parentName ?? null) === (initialParentName ?? null)
          ? prev
          : {
              value,
              label: initialLabel,
              meta: { parentName: initialParentName ?? null },
            },
      );
    }
  }, [value, teamOptions, initialLabel, initialParentName]);

  useEffect(() => {
    if (isOpen) {
      const defaultGroup =
        committedVisibilityGroup ??
        visibilityGroupOptions.find((opt) => opt.value.startsWith("ORG"))
          ?.value ??
        visibilityGroupOptions[0]?.value;
      setStagedVisibilityGroup(defaultGroup);
      setStagedOption(committedOption);
      setStagedSearchQuery("");
    } else {
      setStagedSearchQuery("");
    }
  }, [
    isOpen,
    visibilityGroupOptions,
    committedVisibilityGroup,
    committedOption,
  ]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  const handleConfirm = () => {
    setCommittedOption(stagedOption);
    setCommittedVisibilityGroup(stagedVisibilityGroup);
    onConfirm(stagedOption, stagedVisibilityGroup ?? "");
    setIsOpen(false);
  };

  const handleClear = () => {
    setCommittedOption(null);
    setCommittedVisibilityGroup(undefined);
    onClear?.();
  };

  const handleRosterCreated = (roster: {
    id: string;
    name: string;
    teamName: string;
  }) => {
    console.log("[TeamRosterPicker] New roster created", {
      rosterName: roster.name,
      teamName: roster.teamName,
      rosterId: roster.id,
    });
    const newOption: TeamOption = {
      value: roster.id,
      label: roster.name,
      meta: { parentName: roster.teamName },
    };
    console.log("[TeamRosterPicker] Setting staged option with parentName", {
      label: newOption.label,
      parentName: newOption.meta.parentName,
    });
    setStagedOption(newOption);
  };

  return (
    <div>
      {!isExternallyControlled && (
        <div>
          <Label className="text-sm mb-2">
            {label}
            {required && <span className="text-red-500">*</span>}
            {optional && (
              <span className="text-gray-400 font-regular"> (Optional)</span>
            )}
          </Label>

          {committedOption ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3 justify-between">
                <div className="inline-flex items-center gap-2 rounded bg-gray-100 pl-3 pr-0.5 py-0.5 text-sm">
                  <span className="font-bold">{committedOption.label}</span>
                  {committedOption.meta?.parentName && (
                    <span className="text-text-light">
                      ({committedOption.meta.parentName})
                    </span>
                  )}

                  <Button onClick={handleClear} variant="ghost" size="icon-sm">
                    <Icons.X />
                  </Button>
                </div>

                <Button
                  onClick={() => setIsColorPickerOpen(true)}
                  variant="secondary-flat-dark-outline"
                  size="sm"
                  className="rounded-full gap-4"
                >
                  <span>Select Color</span>
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: `#${teamColor}` }}
                  />
                </Button>
              </div>

              <Button
                variant="ghost"
                size="sm"
                className="text-blue -ml-2 w-fit"
                onClick={() => handleOpenChange(true)}
              >
                Change {label}
              </Button>
            </div>
          ) : (
            <Button
              variant="secondary-flat-dark-outline"
              size="sm"
              type="button"
              onClick={() => handleOpenChange(true)}
            >
              Select {label}
            </Button>
          )}

          {messageSlot}
        </div>
      )}

      <Dialog
        open={isOpen}
        onOpenChange={handleOpenChange}
        size="lg"
        title={label}
        primaryAction={{
          label: "Done",
          onClick: handleConfirm,
          isDisabled: !stagedOption,
        }}
      >
        <div className="flex flex-col gap-4">
          <div>
            <Label className="text-sm mb-2">Source</Label>
            <Select
              items={visibilityGroupOptions}
              value={stagedVisibilityGroup}
              onValueChange={(v) => {
                setStagedVisibilityGroup(v);
                setStagedOption(null);
              }}
              placeholder="Select a source"
            />
          </div>

          <div>
            <Label className="text-sm mb-2">
              Select Team Roster
              {required && <span className="text-red-500">*</span>}
            </Label>
            <AutocompleteSelect
              variant="dark"
              items={teamOptions}
              selectedOption={stagedOption}
              onSelectedValueChange={(v) => {
                const option = teamOptions.find((o) => o.value === v) ?? null;
                setStagedOption(option);
              }}
              isLoading={isLoadingTeams}
              searchValue={stagedSearchQuery}
              onSearchValueChange={setStagedSearchQuery}
              onSearchDebouncedValueChange={() => {}}
              placeholder={`Select ${label.toLowerCase()}`}
              emptyMessage="No teams found"
              isDisabled={!stagedVisibilityGroup}
              itemRenderer={(option) => (
                <div>
                  {option.label}{" "}
                  {option.meta?.parentName ? `(${option.meta.parentName})` : ""}
                </div>
              )}
            />
          </div>

          {stagedVisibilityGroup &&
            isOrgVisibilityGroup(stagedVisibilityGroup) && (
              <CreateTeamRosterDialog
                onSuccess={handleRosterCreated}
                organizationId={stagedVisibilityGroup}
              />
            )}
        </div>
      </Dialog>

      <TeamColorPickerModal
        teamName={committedOption?.label ?? label}
        currentColor={teamColor}
        isOpen={isColorPickerOpen}
        onOpenChange={setIsColorPickerOpen}
        onColorSelect={(color) => {
          onColorChange?.(color);
        }}
      />
    </div>
  );
}
